import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, ArrowLeft, MapPin, SkipForward } from 'lucide-react';
import { Logo } from '../../Logo';

export const NewLocationSubScreen = () => {
  const { data, updateExpansionDetails, nextStep, prevStep, skipSubScreen } = useQuickscan();
  
  const [locationData, setLocationData] = useState({
    location: data.expansionDetails.location?.location || '',
    ownership: data.expansionDetails.location?.ownership || 'rent' as 'buy' | 'rent',
    surfaceArea: data.expansionDetails.location?.surfaceArea || 500,
    purpose: data.expansionDetails.location?.purpose || ''
  });

  const handleNext = () => {
    updateExpansionDetails?.('location', locationData);
    nextStep();
  };

  const handleSkip = () => {
    console.log('Skipping new location details');
    skipSubScreen?.();
  };

  const purposes = [
    'Kantoor',
    'Productie',
    'Opslag/Magazijn',
    'Showroom',
    'Datacenter',
    'Gemengd gebruik'
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb with logo */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-600">
          <span>Expansieplannen</span> <span className="mx-2">{'>'}</span> <span className="text-energy-blue font-medium">Nieuwe locatie</span>
        </div>
        <Logo className="h-6 object-contain" />
      </div>

      <div className="text-center mb-8">
        <MapPin className="w-12 h-12 text-energy-blue mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Details nieuwe locatie
        </h2>
        <p className="text-gray-600">
          Deze informatie helpt ons om passende energieoplossingen voor te stellen
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* Location Input */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Locatie (provincie/regio) <span className="text-red-500">*</span>
          </label>
          <Input
            value={locationData.location}
            onChange={(e) => setLocationData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="Bijv. Noord-Holland, Utrecht, etc."
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">
            Voor netcongestie-analyse en regionale energieoplossingen
          </p>
        </div>

        {/* Ownership */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Eigendom <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: 'rent', label: 'Huren', description: 'Tijdelijke of flexibele oplossingen' },
              { value: 'buy', label: 'Kopen', description: 'Permanente en uitgebreide investeringen' }
            ].map((option) => (
              <div
                key={option.value}
                onClick={() => setLocationData(prev => ({ ...prev, ownership: option.value as 'buy' | 'rent' }))}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  locationData.ownership === option.value
                    ? 'border-energy-blue bg-energy-blue/5' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                <h3 className="font-semibold mb-1">{option.label}</h3>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Surface Area */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Oppervlakte (m²) <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            <Input
              type="number"
              value={locationData.surfaceArea}
              onChange={(e) => setLocationData(prev => ({ ...prev, surfaceArea: parseInt(e.target.value) || 0 }))}
              min="50"
              max="50000"
              className="w-full"
            />
            <div className="text-xs text-gray-500">
              Huidige waarde: {locationData.surfaceArea.toLocaleString()} m²
            </div>
          </div>
        </div>

        {/* Purpose */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Gebruiksdoel <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {purposes.map((purpose) => (
              <button
                key={purpose}
                onClick={() => setLocationData(prev => ({ ...prev, purpose }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  locationData.purpose === purpose
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {purpose}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>

        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleSkip}
            className="border-gray-400 text-gray-600 hover:bg-gray-50"
          >
            <SkipForward className="w-4 h-4 mr-1" /> Overslaan
          </Button>
          
          <Button 
            onClick={handleNext}
            disabled={!locationData.location || !locationData.purpose}
            className="bg-[#014336] text-white hover:bg-[#014336]/90"
          >
            Volgende <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
